import classNames from 'classnames';
import { useRef, useState } from 'react';
import { BaseTranslations } from '../../../../baseTranslations';
import { PushDataLayerFn } from '../../../../types/pushDataLayer';
import { getUtmParams } from '../../../../utils/carInsurance/getUtmParams';
import { isVehicleSelectionComplete } from '../../../../utils/carInsurance/isVehicleSelectionComplete';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { useSafeSearchParams } from '../../../../utils/useSafeSearchParams';
import Button from '../../../Button';
import { nextButtonStyles, disabledButton } from '../../constants';
import { CarInsuranceLeadData } from '../../context';
import { useSafeCarInsuranceContext } from '../../context/useSafeCarInsurance';
import { FormInput } from '../../Form/FormInput';
import { RadioButtonGroup } from '../../Inputs/RadioGroup';
import { useCarInsuranceCentralModal } from '../../WhatsappStickyModule';
import { useCentralModals } from '../useSuccessErrorModal';

export const ContactFormMoneymax: React.FC = () => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  const carInsuranceContext = useSafeCarInsuranceContext();
  if (!carInsuranceContext) {
    return null;
  }
  const {
    requestParams: {
      user: { setValue: setValueUser, value: valueUser },
    },
  } = carInsuranceContext;

  const { email, firstName, lastName, phone } = valueUser;
  const updateContact = (updatedValue: Partial<typeof valueUser>) => {
    const contact = {
      ...valueUser,
      ...updatedValue,
    };

    setValueUser(contact);
  };

  return (
    <>
      <FormInput
        label={staticText.first_name}
        value={firstName}
        placeholder={staticText.first_name}
        type="text"
        onChange={(value) => updateContact({ firstName: value })}
      />
      <FormInput
        label={staticText.last_name}
        value={lastName}
        placeholder={staticText.last_name}
        type="text"
        onChange={(value) => updateContact({ lastName: value })}
      />
      <FormInput
        label={staticText.email}
        value={email}
        placeholder={staticText.email}
        type="text"
        onChange={(value) => updateContact({ email: value })}
      />
      <FormInput
        label={staticText.phone_number}
        value={phone}
        placeholder={staticText.phone_number}
        type="text"
        onChange={(value) => updateContact({ phone: value })}
      />
    </>
  );
};

type ContactDetailsMoneymaxProps = {
  onBackword: () => void;
  pushDataLayer: PushDataLayerFn;
};

export const ContactDetailsMoneymax: React.FC<ContactDetailsMoneymaxProps> = ({
  onBackword,
  pushDataLayer,
}) => {
  const context = useCmsClientContext();

  const staticText = getClientTranslations(BaseTranslations, context);
  const searchParams = useSafeSearchParams();
  const { closeModal } = useCarInsuranceCentralModal('CAR_INSURANCE');
  const { openErrorModal, openConfirmationModal } = useCentralModals();
  const carInsuranceContext = useSafeCarInsuranceContext();
  // The ref stops a second click before the re-render; the state disables the button.
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!carInsuranceContext) {
    return null;
  }
  const {
    form: { isChanged },
    data: { isVehiclesDataLoading, isTrimsLoading },
    requestParams: {
      user: { value: valueUser },
      vehicle: { value: vehicle },
      prices: {
        value: { personalAccident, propertyDamage, isActsOfNature },
      },
    },
    sendLeadToZoho,
    getCarInsuranceLayoutId,
  } = carInsuranceContext;

  const { email, firstName, lastName, phone } = valueUser;
  const { makeName, modelCode, trim, type, manufactureYear, fairMarket } =
    vehicle;

  const optionsPromos = [
    {
      value: 'yes',
      label: staticText.receive_updates,
    },
    {
      value: 'no',
      label: staticText.no_thank,
    },
  ];

  const contactButton =
    Boolean(email?.trim()) &&
    Boolean(firstName?.trim()) &&
    Boolean(lastName?.trim()) &&
    Boolean(phone?.trim()) &&
    // No lead for a vehicle the bar has emptied since the window opened, nor while its
    // quotes, its lists or a previous lead are still in flight (CMS-2047).
    !isChanged &&
    !isVehiclesDataLoading &&
    !isTrimsLoading &&
    !isSubmitting &&
    isVehicleSelectionComplete(vehicle);

  // history.pushState fires neither hashchange nor popstate: after the last
  // modal write, make the hash-state readers re-read the URL (CMS-2047).
  const syncHashModals = () =>
    window.dispatchEvent(
      new HashChangeEvent('hashchange', {
        oldURL: window.location.href,
        newURL: window.location.href,
      }),
    );

  const handleContactMeClick = async () => {
    if (isSubmittingRef.current) return;

    pushDataLayer({
      event: 'form_interact',
      lead_form_name: 'ci-rp-contact-us-form',
      form_type: 'lead form',
      action: 'click',
      component_type: 'button',
      component_title: 'Contact Me',
      vertical_code: 'CI',
    });

    // Armed after the only call that can throw before the try, so `finally`
    // always resets it; still synchronous, so a same-tick second click is blocked.
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const utm = getUtmParams(searchParams);

      const leadMoneymaxData: CarInsuranceLeadData = {
        First_Name: firstName?.trim() || '-',
        Last_Name: lastName?.trim() || '-',
        Email: email?.trim() || '',
        Phone: phone?.trim() || '',
        Product_Type: 'Motor Vehicle',
        Vehicle_Type: type,
        Car_Make: makeName,
        Car_Model: modelCode,
        Car_Year: manufactureYear?.toString() ?? '',
        Car_Variant: trim,
        Bodily_Injury: propertyDamage?.toString() ?? '',
        Property_Damage: propertyDamage?.toString() ?? '',
        Personal_Accident: personalAccident?.toString() ?? '',
        Acts_of_Nature_Coverage:
          typeof isActsOfNature === 'undefined'
            ? null
            : isActsOfNature
              ? 'With AON'
              : 'Without AON',
        FMV: fairMarket?.toString() ?? '',
        UtmSource: utm.utm_source ?? '',
        UtmMedium: utm.utm_medium ?? '',
        UtmCampaign: utm.utm_campaign ?? '',
        UtmContent: utm.utm_content ?? '',
        UtmTerm: utm.utm_term ?? '',
        Lead_Status: 'New Lead',
        isAutoCreated: true,
        // Without an explicit Zoho Layout, Motor Vehicle leads default to the
        // Banking module and never reach the Car Insurance blueprint (ITSM-80971).
        Layout: {
          id: await getCarInsuranceLayoutId(),
        },
        // The Car Insurance layout requires Selected_Product. This flow has no
        // chosen product (contact request, not an apply), so send the same '-'
        // placeholder the legacy QuoteDetailsForm uses (ITSM-80971).
        Selected_Product: '-',
      };

      const res = await sendLeadToZoho(leadMoneymaxData);

      pushDataLayer({
        event: 'form_submit',
        lead_form_name: 'ci-rp-contact-us-form',
        form_type: 'lead form',
        vertical_code: 'CI',
      });

      if (res.leadId) {
        closeModal();
        openConfirmationModal();
        syncHashModals();
      } else {
        closeModal();
        openErrorModal();
        syncHashModals();
        return false;
      }
    } catch (error) {
      pushDataLayer({
        event: 'form_error',
        lead_form_name: 'ci-rp-contact-us-form',
        form_type: 'lead form',
        vertical_code: 'CI',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      closeModal();
      openErrorModal();
      syncHashModals();
      return false;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handlePromoChange = () => {};

  return (
    <div
      className="mt-4 flex w-full flex-col gap-4"
      data-component="contact-details-moneymax"
    >
      <ContactFormMoneymax />

      <RadioButtonGroup
        defaultCheckedOption="yes"
        options={optionsPromos}
        onClick={handlePromoChange}
        label={staticText.offence_free || ''}
        name="offenceFree"
        stacked={true}
      />
      <div className="flex gap-2">
        <Button
          styleType="neutral-light-and-blue"
          variant="outline"
          onClick={onBackword}
          className="h-9 flex-1 text-base"
        >
          {staticText.back}
        </Button>
        <Button
          className={classNames(
            contactButton ? nextButtonStyles : disabledButton,
            'flex-1',
          )}
          disabled={!contactButton}
          onClick={handleContactMeClick}
        >
          {staticText.get_result}
        </Button>
      </div>
    </div>
  );
};
